'use client';

import {useState, useRef, useEffect, useCallback} from 'react';
import {useLocale} from 'next-intl';

interface SearchResult {
  type: string;
  id: number;
  titre: string;
  titre_ar?: string;
  sous_titre: string;
  url: string;
}

const typeIcons: Record<string, string> = {
  projet: '📋',
  gouvernorat: '🗺️',
  delegation: '📍',
  article: '📰',
};

export default function SearchBar() {
  const locale = useLocale();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<SearchResult[]>([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [gouvId, setGouvId] = useState<number | null>(null);
  const [gouvernorats, setGouvernorats] = useState<{id: number; nom_fr: string; nom_ar: string}[]>([]);
  const [showGeoFilter, setShowGeoFilter] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  // Charger la liste des gouvernorats au premier clic filtre
  useEffect(() => {
    if (!showGeoFilter || gouvernorats.length > 0) return;
    const apiUrl = process.env.NEXT_PUBLIC_API_URL || '';
    fetch(`${apiUrl}/api/v1/geo/gouvernorats`)
      .then(r => r.ok ? r.json() : [])
      .then(setGouvernorats)
      .catch(() => {});
  }, [showGeoFilter, gouvernorats.length]);

  const doSearch = useCallback(async (q: string, gId?: number | null) => {
    if (q.length < 2) { setResults([]); return; }
    setLoading(true);
    try {
      const apiUrl = process.env.NEXT_PUBLIC_API_URL || '';
      const gParam = (gId ?? gouvId) ? `&gouvernorat_id=${gId ?? gouvId}` : '';
      const r = await fetch(`${apiUrl}/api/v1/search/?q=${encodeURIComponent(q)}&limit=10${gParam}`);
      if (r.ok) {
        const data = await r.json();
        setResults(data.results || []);
        setOpen(true);
      }
    } catch { /* */ }
    setLoading(false);
  }, [gouvId]);

  const handleInput = (value: string) => {
    setQuery(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => doSearch(value), 300);
  };

  // Close on click outside
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  return (
    <div ref={panelRef} className="relative">
      <div className="flex items-center bg-surface/80 rounded-lg border border-border px-2.5 py-1.5 focus-within:ring-2 focus-within:ring-primary/30 focus-within:border-primary transition-all">
        <svg className="w-4 h-4 text-muted shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
        </svg>
        <input
          ref={inputRef}
          type="search"
          value={query}
          onChange={(e) => handleInput(e.target.value)}
          onFocus={() => results.length > 0 && setOpen(true)}
          placeholder={locale === 'ar' ? 'بحث...' : 'Rechercher...'}
          className="bg-transparent border-none outline-none focus:ring-2 focus:ring-primary/20 rounded text-xs w-28 lg:w-40 px-2 text-foreground placeholder:text-muted"
        />
        {loading && <div className="w-3 h-3 border-2 border-primary border-t-transparent rounded-full animate-spin" />}
        <button
          type="button"
          onClick={() => setShowGeoFilter(!showGeoFilter)}
          className={`p-1 rounded transition-colors cursor-pointer ${gouvId ? 'text-primary' : 'text-muted hover:text-foreground'}`}
          title={locale === 'ar' ? 'تصفية جغرافية' : 'Filtre géographique'}
        >
          <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
          </svg>
        </button>
      </div>

      {/* Filtre geo dropdown */}
      {showGeoFilter && (
        <div className="absolute top-full mt-1 left-0 w-56 bg-white shadow-xl border border-border rounded-lg overflow-hidden z-50 max-h-60 overflow-y-auto">
          <button
            onClick={() => { setGouvId(null); setShowGeoFilter(false); if (query.length >= 2) doSearch(query, null); }}
            className={`w-full text-left px-3 py-2 text-sm hover:bg-surface transition-colors cursor-pointer ${!gouvId ? 'font-semibold text-primary' : ''}`}
          >
            {locale === 'ar' ? 'كل الولايات' : 'Tous les gouvernorats'}
          </button>
          {gouvernorats.map(g => (
            <button
              key={g.id}
              onClick={() => { setGouvId(g.id); setShowGeoFilter(false); if (query.length >= 2) doSearch(query, g.id); }}
              className={`w-full text-left px-3 py-2 text-sm hover:bg-surface transition-colors cursor-pointer ${gouvId === g.id ? 'font-semibold text-primary bg-primary/5' : ''}`}
            >
              {locale === 'ar' ? g.nom_ar : g.nom_fr}
            </button>
          ))}
        </div>
      )}

      {/* Dropdown results */}
      {open && results.length > 0 && (
        <div className="absolute top-full mt-1 left-0 right-0 w-72 bg-white shadow-xl border border-border rounded-lg overflow-hidden z-50 max-h-80 overflow-y-auto">
          {results.map((r, i) => (
            <a
              key={`${r.type}-${r.id}-${i}`}
              href={`/${locale}${r.url}`}
              className="flex items-start gap-2.5 px-3 py-2.5 hover:bg-surface transition-colors border-b border-border/50 last:border-0"
              onClick={() => setOpen(false)}
              style={{textDecorationColor: 'transparent'}}
            >
              <span className="text-base mt-0.5">{typeIcons[r.type] || '📋'}</span>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-foreground truncate" dir="auto">
                  {locale === 'ar' && r.titre_ar ? r.titre_ar : r.titre}
                </div>
                <div className="text-[10px] text-muted">{r.sous_titre}</div>
              </div>
              <span className="text-[9px] text-muted/60 uppercase shrink-0 mt-1">{r.type}</span>
            </a>
          ))}
        </div>
      )}

      {open && query.length >= 2 && results.length === 0 && !loading && (
        <div className="absolute top-full mt-1 left-0 w-72 bg-white shadow-xl border border-border rounded-lg p-4 z-50 text-center text-sm text-muted">
          {locale === 'ar' ? 'لا نتائج لـ' : 'Aucun résultat pour'} &quot;{query}&quot;
        </div>
      )}
    </div>
  );
}
